/*
 * <cubica-listing>: collection and search listings without reloads (S38 parity).
 * Filters, chips and sort re-render the section in place (Section Rendering API)
 * and replaceState the URL; "Ver más" / "Ver anteriores" add a page to the grid.
 * Without JS every control is a link or a GET form, so nothing depends on this.
 */
(() => {
  if (customElements.get('cubica-listing')) return;
  const MY_SIZE = 'pangea.mySize.v1';
  const store = {
    get: () => {
      try {
        return localStorage.getItem(MY_SIZE);
      } catch {
        return null;
      }
    },
    set: (value) => {
      try {
        localStorage.setItem(MY_SIZE, value);
      } catch {}
    },
  };

  class CubicaListing extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.showMySize();
      this.addEventListener('click', (event) => {
        const page = event.target.closest('[data-listing-page]');
        if (page) {
          event.preventDefault();
          return this.page(page);
        }
        const link = event.target.closest('a[data-listing-link]');
        if (link) {
          event.preventDefault();
          this.render(link.href);
        }
      });
      // Price slider: move the fill and labels while dragging; the hidden gte/lte fields refine on release (change).
      this.addEventListener('input', (event) => {
        const thumb = event.target.closest('[data-range]');
        if (!thumb) return;
        const root = thumb.closest('[data-price-range]');
        const [min, max] = root.querySelectorAll('[data-range]');
        if (Number(min.value) > Number(max.value)) thumb.value = thumb === min ? max.value : min.value;
        const top = Number(max.max);
        const number = new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 });
        root.querySelector('.c-range').style.setProperty('--from', min.value / top);
        root.querySelector('.c-range').style.setProperty('--to', max.value / top);
        for (const [input, edge] of [[min, '0'], [max, max.max]]) {
          const text = `${number.format(input.value)} ${root.dataset.currency}`;
          input.setAttribute('aria-valuetext', text);
          root.querySelector(`[data-range-label="${input.dataset.range}"]`).textContent = text;
          root.querySelector(`[data-range-field="${input.dataset.range}"]`).value = input.value === edge ? '' : input.value;
        }
      });
      this.addEventListener('change', (event) => {
        const input = event.target;
        if (input.type === 'checkbox' && input.checked && input.closest('[data-size-filter]')) store.set(input.dataset.label);
        if (input.name === 'sort_by' || input.closest('[data-listing-form]')) {
          event.stopPropagation();
          this.render(this.formUrl());
        }
      });
      this.addEventListener('submit', (event) => {
        if (!event.target.matches('[data-listing-form]')) return;
        event.preventDefault();
        event.target.closest('dialog')?.close();
      });
    }

    formUrl() {
      const form = this.querySelector('[data-listing-form]');
      const sort = this.querySelector('select[name="sort_by"]');
      const url = new URL(form?.action || location.href, location.origin);
      const params = form ? new FormData(form) : new URLSearchParams(location.search);
      url.search = '';
      for (const [key, value] of params) if (value !== '' && key !== 'page' && key !== 'sort_by') url.searchParams.append(key, value);
      if (sort) url.searchParams.set('sort_by', sort.value);
      return url.href;
    }

    async fetchSection(href) {
      const url = new URL(href, location.origin);
      url.searchParams.set('section_id', this.sectionId);
      const response = await fetch(url);
      if (!response.ok) throw new Error(response.status);
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    }

    async render(href) {
      const results = this.querySelector('.c-listing__results');
      const focusId = document.activeElement?.id;
      results?.setAttribute('aria-busy', 'true');
      try {
        const doc = await this.fetchSection(href);
        this.querySelectorAll('[data-swap]').forEach((region) => {
          const next = doc.querySelector(`[data-swap="${region.dataset.swap}"]`);
          if (next) region.replaceChildren(...next.childNodes);
        });
        const url = new URL(href, location.origin);
        url.searchParams.delete('section_id');
        history.replaceState(history.state, '', url.pathname + url.search);
        this.showMySize();
        if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
      } catch {
        location.href = href;
      } finally {
        results?.removeAttribute('aria-busy');
      }
    }

    async page(button) {
      if (button.getAttribute('aria-busy') === 'true') return;
      const previous = button.dataset.listingPage === 'previous';
      button.setAttribute('aria-busy', 'true');
      try {
        const doc = await this.fetchSection(button.href);
        const grid = this.querySelector('[data-listing-grid]');
        const items = [...(doc.querySelector('[data-listing-grid]')?.children || [])];
        if (previous) {
          const anchor = grid.firstElementChild;
          const top = anchor?.getBoundingClientRect().top;
          grid.prepend(...items);
          if (anchor) scrollBy(0, anchor.getBoundingClientRect().top - top);
          const block = doc.querySelector('[data-listing-previous]');
          const current = this.querySelector('[data-listing-previous]');
          block ? current.replaceWith(block) : current.remove();
        } else {
          grid.append(...items);
          this.querySelector('[data-listing-more]').replaceWith(doc.querySelector('[data-listing-more]'));
          const url = new URL(button.href, location.origin);
          history.replaceState(history.state, '', url.pathname + url.search);
        }
        items[0]?.querySelector('a[href]')?.focus({ preventScroll: true });
      } catch {
        location.href = button.href;
      }
    }

    showMySize() {
      const size = store.get();
      if (!size) return;
      const chip = [...this.querySelectorAll('[data-my-size]')].find((el) => el.dataset.mySize === size);
      if (chip) chip.hidden = false;
    }
  }

  customElements.define('cubica-listing', CubicaListing);
})();
